<section class="test-plans-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'testPlans.list.headline' | translate" [subtitle]="'testPlans.list.subtitle' | translate">
    <ng-container right>
      <app-stat-group [items]="stats()" />
      @if (!isReadOnly()) {
        <button type="button" class="test-plans-page__create-btn" data-testid="tp-liste-anlegen" (click)="onCreate()">
          <span class="test-plans-page__create-btn-icon" aria-hidden="true">＋</span>
          {{ 'testPlans.list.create' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="test-plans-page__toolbar">
    <app-search-bar
      data-testid="testplan-search"
      [placeholder]="'testPlans.list.search' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-filter-dropdown
      data-testid="testplan-release-filter"
      name="release"
      size="tall"
      [value]="releaseFilterValue()"
      [placeholder]="'testPlans.list.filter.allReleases' | translate"
      [options]="releaseDropdownOptions()"
      (valueChange)="setReleaseFilter($event)"
    />
    <app-filter-dropdown
      data-testid="testplan-status-filter"
      name="status"
      size="tall"
      [value]="statusFilterValue()"
      [placeholder]="'testPlans.list.filter.allStatuses' | translate"
      [options]="statusDropdownOptions()"
      (valueChange)="setStatusFilter($event)"
    />
    <app-filter-dropdown
      data-testid="testplan-type-filter"
      name="type"
      size="tall"
      [value]="typeFilterValue()"
      [placeholder]="'testPlans.list.filter.allTypes' | translate"
      [options]="typeDropdownOptions()"
      (valueChange)="setTypeFilter($event)"
    />
    <app-filter-dropdown
      data-testid="testplan-assignee-filter"
      name="assignee"
      size="tall"
      [value]="assigneeFilterValue()"
      [placeholder]="'testPlans.list.filter.allAssignees' | translate"
      [options]="assigneeDropdownOptions()"
      (valueChange)="setAssigneeFilter($event)"
    />
    <app-tag-filter
      data-testid="testplan-tag-filter"
      [tags]="tagOptions()"
      [value]="tagFilter()"
      [ariaLabel]="'testPlans.list.filter.tagsAriaLabel' | translate"
      (valueChange)="setTagFilter($event)"
    />
    <!-- Rollout NEX-787: geteilter Ansichten-Baustein aus NEX-786, rechts neben den Filter-Chips
         (Design-Referenz page-testplaene.jsx:239-263, Bundle c7616651204). Ersetzt die vormals
         lokale, nur-localStorage-basierte app-saved-views-Komponente (NEX-803/804). -->
    <app-saved-views-menu
      data-testid="testplan-saved-views"
      [views]="savedViewsOptions()"
      [activeName]="activeSavedViewName()"
      [context]="'TEST_PLANS'"
      [projectId]="projectId"
      (viewSelected)="onSavedViewSelected($event)"
      (viewSaved)="onSavedViewSaved($event)"
      (viewDeleted)="onSavedViewDeleted($event)"
      (viewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    @if (hasFilters()) {
      <app-reset-filter-button [label]="'testPlans.list.filter.reset' | translate" (resetClick)="resetFilters()" />
    }
    <div class="test-plans-page__toolbar-spacer"></div>
    <ul class="test-plans-page__legend">
      @for (entry of legend; track entry) {
        <li class="test-plans-page__legend-item">
          <span
            class="test-plans-page__legend-swatch"
            [class]="'test-plans-page__legend-swatch--' + entry"
            aria-hidden="true"
          ></span>
          {{ 'testPlans.list.legend.' + entry | translate }}
        </li>
      }
    </ul>
    <span class="test-plans-page__count" data-testid="testplan-counter">
      {{ resultCountKey() | translate: { count: total(), total: unfilteredTotal() } }}
    </span>
  </div>

  @if (savedViewsError()) {
    <p class="test-plans-page__hint test-plans-page__hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <p class="test-plans-page__hint">{{ 'testPlans.list.loading' | translate }}</p>
  } @else if (loadError()) {
    <p class="test-plans-page__hint">{{ 'testPlans.list.loadError' | translate }}</p>
  } @else {
    <div
      appListFocusScroll
      class="test-plans-table"
      role="table"
      [attr.aria-label]="'testPlans.list.tableAria' | translate"
    >
      <div class="test-plans-table__head" role="row">
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--id"
          role="columnheader"
          [attr.aria-sort]="ariaSort('publicId')"
          (click)="toggleSort('publicId')"
        >
          {{ 'testPlans.list.columns.id' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--title"
          role="columnheader"
          [attr.aria-sort]="ariaSort('title')"
          (click)="toggleSort('title')"
        >
          {{ 'testPlans.list.columns.title' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--release"
          role="columnheader"
          [attr.aria-sort]="ariaSort('release')"
          (click)="toggleSort('release')"
        >
          {{ 'testPlans.list.columns.release' | translate }}
        </button>
        <div class="test-plans-table__th test-plans-table__th--type" role="columnheader">
          {{ 'testPlans.list.columns.type' | translate }}
        </div>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--status"
          role="columnheader"
          [attr.aria-sort]="ariaSort('status')"
          (click)="toggleSort('status')"
        >
          {{ 'testPlans.list.columns.status' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--assignee"
          role="columnheader"
          [attr.aria-sort]="ariaSort('assignee')"
          (click)="toggleSort('assignee')"
        >
          {{ 'testPlans.list.columns.assignee' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--progress"
          role="columnheader"
          [attr.aria-sort]="ariaSort('progress')"
          (click)="toggleSort('progress')"
        >
          {{ 'testPlans.list.columns.progress' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--due"
          role="columnheader"
          [attr.aria-sort]="ariaSort('dueDate')"
          (click)="toggleSort('dueDate')"
        >
          {{ 'testPlans.list.columns.due' | translate }}
        </button>
        <div class="test-plans-table__th test-plans-table__th--actions" role="columnheader">
          {{ 'testPlans.list.columns.actions' | translate }}
        </div>
      </div>

      @if (filteredPlans().length === 0) {
        <div class="test-plans-table__empty" role="row">
          <div role="cell">
            <div class="test-plans-table__empty-title">{{ 'testPlans.list.emptyTitle' | translate }}</div>
            <div class="test-plans-table__empty-text">
              {{ (hasFilters() ? 'testPlans.list.emptyFiltered' : 'testPlans.list.emptyNoPlans') | translate }}
            </div>
            @if (hasFilters()) {
              <button
                type="button"
                class="test-plans-table__empty-reset"
                data-testid="testplan-empty-reset"
                (click)="resetFilters()"
              >
                {{ 'testPlans.list.filter.reset' | translate }}
              </button>
            }
          </div>
        </div>
      }

      @for (plan of pagedPlans(); track trackById($index, plan)) {
        @let runs = runSummaryOf(plan);
        @let overdue = isPlanOverdue(plan);
        @let type = planType(plan);
        @let typeLabel = 'testPlans.type.' + type | translate;
        <div
          class="test-plans-table__row"
          data-testid="testplan-row"
          role="row"
          tabindex="0"
          [attr.aria-label]="plan.title"
          (click)="onOpenRow(plan)"
          (keydown.enter)="onRowKeydown($event, plan)"
          (keydown.space)="onRowKeydown($event, plan)"
        >
          <div class="test-plans-table__td test-plans-table__td--id" role="cell">
            @if (plan.publicId) {
              <app-code-id-chip [id]="plan.publicId" />
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--title" role="cell">
            <div class="test-plans-table__title">{{ plan.title }}</div>
            <div class="test-plans-table__meta">
              <span class="test-plans-table__case-count">
                {{ plan.testCaseIds.length }} {{ 'testPlans.list.testcasesSuffix' | translate }}
              </span>
              @if (type === 'REGRESSION') {
                <span class="test-plans-table__mode test-plans-table__mode--regression" data-testid="testplan-type-tag">
                  {{ typeLabel }}
                </span>
              }
              @switch (plan.executionMode) {
                @case ('MANUAL') {
                  <span class="test-plans-table__mode test-plans-table__mode--manual">
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">manage_accounts</mat-icon>
                    {{ 'testPlans.list.executionMode.MANUAL' | translate }}
                  </span>
                }
                @case ('AUTOMATED') {
                  <span class="test-plans-table__mode test-plans-table__mode--automated">
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">settings</mat-icon>
                    {{ 'testPlans.list.executionMode.AUTOMATED' | translate }}
                  </span>
                }
                @case ('MIXED') {
                  <span
                    class="test-plans-table__mode test-plans-table__mode--mixed"
                    [attr.title]="'testPlans.list.executionMode.mixedTooltip' | translate"
                  >
                    <span aria-hidden="true">⚠</span>
                    {{ 'testPlans.list.executionMode.MIXED' | translate }}
                  </span>
                }
              }
            </div>
          </div>
          <div class="test-plans-table__td test-plans-table__td--release" role="cell">
            @if (plan.releaseId) {
              <span class="test-plans-table__release">{{ plan.releaseId }}</span>
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--type" role="cell" [attr.title]="typeLabel">
            <mat-icon
              fontSet="material-icons-outlined"
              aria-hidden="true"
              class="test-plans-table__type-icon"
              [class.test-plans-table__type-icon--regression]="type === 'REGRESSION'"
            >
              {{ type === 'REGRESSION' ? 'bug_report' : 'verified' }}
            </mat-icon>
            <span class="sr-only">{{ typeLabel }}</span>
          </div>
          <div class="test-plans-table__td test-plans-table__td--status" role="cell">
            <app-plan-status-pill [value]="plan.status" />
          </div>
          <div class="test-plans-table__td test-plans-table__td--assignee" role="cell">
            @if (plan.assigneeName) {
              <div class="test-plans-table__assignee">
                <app-avatar [name]="plan.assigneeName" [size]="26" />
                <span class="test-plans-table__assignee-name">{{ plan.assigneeName }}</span>
              </div>
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--progress" role="cell">
            <app-progress-bar
              variant="block"
              [ariaLabel]="'testPlans.list.progressAria' | translate: { percent: progressPercent(plan) }"
              [passed]="plan.progress.passed"
              [failed]="plan.progress.failed"
              [blocked]="plan.progress.blocked"
              [inProgress]="0"
              [open]="plan.progress.open + plan.progress.inProgress"
            />
            <div
              class="test-plans-table__progress-meta"
              [attr.title]="
                runs.lastRunAt
                  ? ('testPlans.list.lastRunTooltip' | translate: { date: runs.lastRunAt | date: 'dd.MM.yyyy' })
                  : null
              "
            >
              {{
                runs.key
                  | translate
                    : { percent: progressPercent(plan), count: runs.count, date: runs.lastRunAt | date: 'dd.MM.' }
              }}
            </div>
          </div>
          <div class="test-plans-table__td test-plans-table__td--due" role="cell">
            @if (plan.dueDate) {
              <div class="test-plans-table__due" [class.test-plans-table__due--overdue]="overdue">
                {{ plan.dueDate | date: 'dd.MM.yyyy' }}
              </div>
              @if (overdue) {
                <div class="test-plans-table__overdue">{{ 'testPlans.list.overdue' | translate }}</div>
              }
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div
            class="test-plans-table__td test-plans-table__td--actions"
            role="cell"
            (click)="$event.stopPropagation()"
            (keydown.enter)="$event.stopPropagation()"
            (keydown.space)="$event.stopPropagation()"
          >
            @if (canStart(plan)) {
              @let startLabel =
                (plan.executionMode === 'MIXED' ? 'testPlans.list.actions.startMixed' : 'testPlans.list.actions.start')
                  | translate;
              <button
                type="button"
                class="test-plans-table__action test-plans-table__action--start"
                [class.test-plans-table__action--accent]="plan.executionMode !== 'MIXED'"
                data-testid="testplan-start-btn"
                [attr.title]="startLabel"
                [attr.aria-label]="startLabel"
                (click)="onAskStart(plan, $event)"
              >
                <span aria-hidden="true">▶</span>
              </button>
            }
            <button
              type="button"
              class="test-plans-table__action test-plans-table__action--accent"
              data-testid="testplan-report-btn"
              [attr.title]="'testPlans.list.actions.report' | translate"
              [attr.aria-label]="'testPlans.list.actions.report' | translate"
              (click)="onOpenReport(plan, $event)"
            >
              <mat-icon fontSet="material-icons-outlined" aria-hidden="true">shield</mat-icon>
            </button>
            @if (!isReadOnly()) {
              <button
                type="button"
                class="test-plans-table__action test-plans-table__action--duplicate"
                data-testid="testplan-duplicate-btn"
                [attr.title]="'testPlans.list.actions.duplicate' | translate"
                [attr.aria-label]="'testPlans.list.actions.duplicate' | translate"
                (click)="onDuplicate(plan, $event)"
              >
                <span aria-hidden="true">⧉</span>
              </button>
              @if (plan.status === 'ABGESCHLOSSEN') {
                <button
                  type="button"
                  class="test-plans-table__action"
                  data-testid="testplan-archive-btn"
                  [attr.title]="'testPlans.list.actions.archive' | translate"
                  [attr.aria-label]="'testPlans.list.actions.archive' | translate"
                  (click)="onAskArchive(plan, $event)"
                >
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">folder</mat-icon>
                </button>
              } @else {
                <button
                  type="button"
                  class="test-plans-table__action test-plans-table__action--danger"
                  data-testid="testplan-delete-btn"
                  [attr.title]="'testPlans.list.actions.delete' | translate"
                  [attr.aria-label]="'testPlans.list.actions.delete' | translate"
                  (click)="onAskDelete(plan, $event)"
                >
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete</mat-icon>
                </button>
              }
            }
          </div>
        </div>
      }
    </div>

    @if (filteredPlans().length > 0) {
      <div class="tp-footer">
        <span class="tp-footer__range" role="status">
          {{ 'testPlans.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() } }}
        </span>
        <div class="tp-footer__pager">
          <label class="tp-footer__label" for="tp-page-size">{{ 'testPlans.footer.rowsPerPage' | translate }}</label>
          <select
            id="tp-page-size"
            class="tp-footer__select"
            (change)="onPageSizeChange(+pageSizeSelect.value)"
            #pageSizeSelect
            [attr.aria-label]="'testPlans.footer.rowsPerPageAriaLabel' | translate"
          >
            @for (size of pageSizeOptions; track size) {
              <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
            }
          </select>
          @if (pageCount() > 1) {
            <div class="tp-footer__nav">
              <button
                type="button"
                class="tp-action-btn"
                [disabled]="pageIndex() === 0"
                [attr.aria-label]="'testPlans.footer.previousPage' | translate"
                (click)="onPageChange(pageIndex() - 1)"
              >
                <mat-icon aria-hidden="true">chevron_left</mat-icon>
              </button>
              <button
                type="button"
                class="tp-action-btn"
                [disabled]="pageIndex() >= pageCount() - 1"
                [attr.aria-label]="'testPlans.footer.nextPage' | translate"
                (click)="onPageChange(pageIndex() + 1)"
              >
                <mat-icon aria-hidden="true">chevron_right</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }
  }

  <app-confirm-dialog
    [visible]="planToDelete() !== null"
    [title]="'testPlans.list.deleteDialog.title' | translate"
    [message]="
      planToDelete()
        ? ('testPlans.list.deleteDialog.message'
          | translate: { title: planToDelete()!.title, id: planToDelete()!.publicId })
        : ''
    "
    [confirmLabel]="'testPlans.list.deleteDialog.confirm' | translate"
    [cancelLabel]="'testPlans.list.deleteDialog.cancel' | translate"
    [busy]="deleting()"
    (confirmed)="onConfirmDelete()"
    (cancelled)="onCancelDelete()"
  >
    @if (deleteError()) {
      <p class="test-plans-page__hint test-plans-page__hint--error" role="alert">{{ deleteErrorKey() | translate }}</p>
    }
  </app-confirm-dialog>

  <app-confirm-dialog
    icon="edit"
    iconTon="akzent"
    confirmColor="primary"
    [visible]="mixedStartPlan() !== null"
    [title]="'testPlans.list.mixedDialog.title' | translate"
    [message]="
      mixedStartPlan()
        ? ('testPlans.list.mixedDialog.message'
          | translate: { title: mixedStartPlan()!.title, id: mixedStartPlan()!.publicId })
        : ''
    "
    [confirmLabel]="'testPlans.list.mixedDialog.confirm' | translate"
    [cancelLabel]="'testPlans.list.mixedDialog.cancel' | translate"
    (confirmed)="onEditMixedPlan()"
    (cancelled)="onCancelStart()"
  />

  <app-confirm-dialog
    icon="edit"
    iconTon="akzent"
    confirmColor="primary"
    [visible]="realRunPlan() !== null"
    [title]="'testPlans.list.startDialog.title' | translate"
    [message]="
      realRunPlan()
        ? ((realRunPlan()!.executionMode === 'AUTOMATED'
            ? 'testPlans.list.startDialog.messageAutomated'
            : 'testPlans.list.startDialog.messageManual'
          )
          | translate
            : {
                id: realRunPlan()!.publicId,
                title: realRunPlan()!.title,
                count: realRunPlan()!.testCaseIds.length,
                release: realRunPlan()!.releaseId ?? '—',
              })
        : ''
    "
    [confirmLabel]="'testPlans.list.startDialog.confirm' | translate"
    [cancelLabel]="'testPlans.list.startDialog.cancel' | translate"
    (confirmed)="onStartRealRun()"
    (cancelled)="onCancelStart()"
  />

  <app-confirm-dialog
    icon="edit"
    iconTon="akzent"
    confirmColor="primary"
    [visible]="planToArchive() !== null"
    [title]="'testPlans.list.archiveDialog.title' | translate"
    [message]="
      planToArchive()
        ? ('testPlans.list.archiveDialog.message'
          | translate: { title: planToArchive()!.title, id: planToArchive()!.publicId })
        : ''
    "
    [confirmLabel]="'testPlans.list.archiveDialog.confirm' | translate"
    [cancelLabel]="'testPlans.list.archiveDialog.cancel' | translate"
    [busy]="archiving()"
    (confirmed)="onConfirmArchive()"
    (cancelled)="onCancelArchive()"
  >
    @if (archiveError(); as errorKey) {
      <p class="test-plans-page__hint test-plans-page__hint--error" role="alert">{{ errorKey | translate }}</p>
    }
  </app-confirm-dialog>
</section>
